import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, Card, DatePicker, Form, notification, Select } from 'antd';
import React, { useEffect } from 'react';
import CustomInput from '../CustomComponents/CustomInput';
import dayjs from 'dayjs';
import { ADD_COST_CLAIM, GET_COST_CLAIM, UPDATE_COST_CLAIM } from './CostClaimQueries';
import { GET_PARTICIPANTS } from '../Party/PartyQueries';
import { useSearchParams } from 'react-router-dom';

interface CostClaimFormProps {
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  proceedingId: string;
  id: string;
}

interface OptionMap {
  value: string;
  label: string;
}

const { Option } = Select;

const CostClaimForm: React.FC<CostClaimFormProps> = ({ onSubmit, onCancel, proceedingId, id }) => {
  const [form] = Form.useForm();
  const isAuthenticated = useIsAuthenticated();
  const isEditForm = id !== null ? true : false;
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');

  const partiesOptions: OptionMap[] = [];
  const [getParticipants, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_PARTICIPANTS);

  const [addCostClaim, { data: result, error: addError }] = useMutation(ADD_COST_CLAIM);
  const [updateCostClaim, { data: updateresult, error: updateError }] =
    useMutation(UPDATE_COST_CLAIM);

  const [getCostClaim, { data: costClaimDetails, error: loadingError }] =
    useLazyQuery(GET_COST_CLAIM);

  useEffect(() => {
    if (tab === '9' && proceedingId) {
      getParticipants({
        variables: {
          input: { proceedingNumber: proceedingId },
        },
      });
    }
  }, [tab]);

  response?.participants?.data.map((participant: any) => {
    partiesOptions.push({
      value: participant.id,
      label: `${participant.partyName}`,
    });
  });

  useEffect(() => {
    form.resetFields();
    if (isEditForm) {
      getCostClaim({
        variables: {
          input: {
            proceedingNumber: proceedingId,
            costClaimId: id,
          },
        },
      });
    }
  }, [proceedingId, id]);
  useEffect(() => {
    if (costClaimDetails) {
      form.setFieldsValue(costClaimDetails?.costClaimDetail);
      form.setFieldValue('partyId', {value: costClaimDetails?.costClaimDetail.partyId})
      form.setFieldValue('costClaimDate', dayjs(costClaimDetails?.costClaimDetail.costClaimDate));
    }
  }, [costClaimDetails]);

  const onFinish = async (values: any) => {
    try {
      if (isEditForm) {
        await updateCostClaim({
          variables: {
            input: {
              id: costClaimDetails?.costClaimDetail.id,
              proceedingNumber: proceedingId,
              costClaimDate: values.costClaimDate,
              costClaimComment: values.costClaimComment,
              partyId: values.partyId.value,
            },
          },
        });
      } else {
        await addCostClaim({
          variables: {
            input: {
              proceedingId: proceedingId,
              partyId: values.partyId.value,
              costClaimDate: values.costClaimDate,
              costClaimComment: values.costClaimComment,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (!isEditForm && result?.addCostClaim.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Cost Claim has been added.`,
      });
      form.resetFields();
      onSubmit(true);
    }
    if (!isEditForm && result?.addCostClaim.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in adding cost Claim to the proceeding.`,
      });
      onSubmit(false);
    }
    if (isEditForm && updateresult?.updateCostClaim.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Cost claim has been updated.`,
      });
      form.resetFields();
      onSubmit(true);
    }
    if (isEditForm && updateresult?.updateCostClaim.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in updating cost claim to the proceeding.`,
      });
      onSubmit(false);
    }
  }, [result, addError, updateresult, updateError]);
  return (
    <>
      <div className="thin-border">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="partyId"
            label="Party Name"
            rules={[{ required: true, message: 'Please provide a value' }]}
          >
            <Select labelInValue disabled={!isAuthenticated}>
              {partiesOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="costClaimDate"
            label="Cost Claim Date"
            rules={[{ required: true, message: 'Please select the date' }]}
          >
            <DatePicker disabled={!isAuthenticated} style={{ width: '100%' }}></DatePicker>
          </Form.Item>
          <Form.Item name="costClaimComment" label="Cost Claim Comment">
            <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
          </Form.Item>
          {isAuthenticated && (
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={onCancel}>
                Cancel
              </Button>
            </Form.Item>
          )}{' '}
          {!isAuthenticated && (
            <Button style={{ marginLeft: 8 }} onClick={onCancel}>
              Back
            </Button>
          )}
        </Form>
      </div>
    </>
  );
};
export default CostClaimForm;
